import {
  createContext,
  useContext,
  useId,
  type CSSProperties,
  type ReactNode,
} from "react";
import { ResponsiveContainer } from "recharts";

export type ChartConfig = Record<
  string,
  { label?: ReactNode; color?: string }
>;

type ChartContextValue = {
  config: ChartConfig;
};

const ChartContext = createContext<ChartContextValue | null>(null);

function chartStyleFromConfig(config: ChartConfig): CSSProperties {
  const style: Record<string, string> = {};
  for (const [key, entry] of Object.entries(config)) {
    if (entry.color) {
      style[`--color-${key}`] = entry.color;
    }
  }
  return style as CSSProperties;
}

export function ChartContainer({
  config,
  className,
  children,
  id,
  style,
  role,
  "aria-label": ariaLabel,
}: {
  config: ChartConfig;
  className?: string;
  children: ReactNode;
  id?: string;
  style?: CSSProperties;
  role?: string;
  "aria-label"?: string;
}) {
  const generatedId = useId();
  const chartId = id ?? generatedId.replace(/:/g, "");

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-chart={chartId}
        className={className}
        role={role}
        aria-label={ariaLabel}
        style={{ ...chartStyleFromConfig(config), ...style }}
      >
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  );
}

export function useChartConfig(): ChartConfig {
  const ctx = useContext(ChartContext);
  if (!ctx) {
    throw new Error("useChartConfig must be used within ChartContainer");
  }
  return ctx.config;
}
